// The admin rail's entries, in their groups (persona audit round 1 + 1b,
// WH-8 — seven personas).
//
// MEASURED (dev 2026-10-24/25): the rail listed 19 entries in one run — a
// clinic's owner read "Client Module", "Feature Flags", "Status", "System
// Health" beside "Users & Roles" — and at 1366x768 the last entries were cut
// below the screen with no scroll.
//
// Four groups, each under its own heading (components/AdminChrome.js
// AdminSidebarGroup, a native disclosure): the workspace itself, its usage and
// activity, its connections, and the developer tools. On a BUSINESS workspace
// the developer group starts folded — never while the page you are on is in
// it. A business workspace is one whose owner told us their work:
// `settings.preset` (an industry preset or "Something else", CONTRACT §1 —
// any non-empty preset is "answered"); a workspace with none keeps every
// group open (a developer's own workspace, or one set up before presets).
//
// The ROW ORDER is part of the contract (the admin page tests pin it):
// Workspace Settings first; Branding right after White-label Domains; Mail
// right after Integrations.
//
// FRANK: each export is self-contained (FA204) — the table lives inside the
// function that returns it; `this` is the calling element when el.call runs it.

// Is the active workspace a business workspace? → true when its owner chose a
// work (an industry preset or "Something else") in the welcome, Fly or the App
// Store.
export function workspaceIsBusiness(root) {
  const r = root && typeof root === 'object' ? root : this && this.state && this.state.root
  const ws = r && r.workspace && typeof r.workspace === 'object' ? r.workspace : null
  const preset = ws && ws.settings && typeof ws.settings === 'object' ? ws.settings.preset : null
  return !!(preset && typeof preset === 'object' && typeof preset.key === 'string' && preset.key)
}

// [{ key, label, foldable, folded, rows: [{ label, href, icon }] }] — the
// groups in rail order. Labels are bundle KEYS (AdminSidebarGroup and
// AdminSidebarLink resolve them).
export function adminRailGroups(root) {
  const r = root && typeof root === 'object' ? root : this && this.state && this.state.root
  const ws = r && r.workspace && typeof r.workspace === 'object' ? r.workspace : null
  const preset = ws && ws.settings && typeof ws.settings === 'object' ? ws.settings.preset : null
  const business = !!(
    preset &&
    typeof preset === 'object' &&
    typeof preset.key === 'string' &&
    preset.key
  )
  // The page on screen, without the /w/<org>/<workspace> prefix.
  const raw = String((r && (r.currentPath || r.route)) || '/').split('?')[0]
  const here = raw.replace(/^\/w\/[^/]+\/[^/]+(?=\/|$)/, '') || '/'
  const groups = [
    {
      key: 'workspace',
      label: 'admin.nav.groupWorkspace',
      rows: [
        // FIRST on purpose (2026-09-02 per Nika): the GENERAL settings page
        // leads, every specialised console follows it.
        {
          label: 'admin.nav.workspaceSettings',
          href: '/admin/workspace-settings',
          icon: 'settings'
        },
        { label: 'admin.nav.usersRoles', href: '/admin', icon: 'user' },
        { label: 'admin.nav.teams', href: '/admin/teams', icon: 'network' },
        { label: 'admin.nav.permissions', href: '/admin/permissions', icon: 'lock' },
        {
          label: 'admin.nav.dashboardDefaults',
          href: '/admin/dashboard-defaults',
          icon: 'grid'
        },
        // White-label domains — `Organization.customDomains`, the branded host
        // the SHELL is served on — and right after it what that host LOOKS
        // like (`Organization.branding`): two halves of one product.
        { label: 'admin.nav.domains', href: '/admin/domains', icon: 'globe' },
        { label: 'admin.nav.branding', href: '/admin/branding', icon: 'colorOutline' },
        // The data export sits beside the danger zone: both act on the whole
        // workspace.
        { label: 'admin.data.nav', href: '/admin/data', icon: 'database' },
        { label: 'admin.nav.danger', href: '/admin/danger', icon: 'exclMark' }
      ]
    },
    {
      key: 'usage',
      label: 'admin.nav.groupUsage',
      rows: [
        { label: 'admin.nav.usage', href: '/admin/usage', icon: 'billing' },
        { label: 'admin.nav.analytics', href: '/admin/analytics', icon: 'analytics' },
        { label: 'admin.nav.activityLogs', href: '/admin/logs', icon: 'clock' },
        {
          label: 'admin.nav.activityScoring',
          href: '/admin/activity-scoring',
          icon: 'ratingStar'
        }
      ]
    },
    {
      key: 'connections',
      label: 'admin.nav.groupConnections',
      rows: [
        { label: 'admin.nav.integrations', href: '/admin/integrations', icon: 'sfPlugin' },
        { label: 'admin.nav.mail', href: '/admin/mail', icon: 'email' }
      ]
    },
    {
      key: 'developer',
      label: 'admin.nav.groupDeveloper',
      foldable: true,
      rows: [
        { label: 'admin.nav.module', href: '/admin/module', icon: 'blocks' },
        { label: 'admin.nav.flags', href: '/admin/flags', icon: 'switching' },
        { label: 'admin.nav.externalData', href: '/data/external', icon: 'database' },
        { label: 'admin.nav.status', href: '/admin/status', icon: 'signal' },
        { label: 'admin.nav.systemHealth', href: '/admin/system', icon: 'signal' }
      ]
    }
  ]
  // THE ROWS FOLLOW EACH PAGE'S OWN GATE (3a, concierge 2026-10-31 23:35):
  // a row shows unless the page it opens sends this person away
  // (functions/roleHelpers.js `adminPageAccess`, the answer the page itself
  // runs). A page still waiting for its inputs keeps its row, as the page
  // keeps the person; a group left with no row is left out. So an org admin
  // reads every row, and a workspace admin who is only an org member reads
  // the workspace's own pages. Asked through the calling element (el.call);
  // a bare call, with no element to ask, lists every row.
  const el = this && typeof this.call === 'function' ? this : null
  const shows = (row) => !el || el.call('adminPageAccess', r, row.href) !== 'closed'
  return groups
    .map((g) => {
      const rows = g.rows.filter(shows)
      const holdsHere = rows.some((row) => row.href === here)
      return {
        key: g.key,
        label: g.label,
        foldable: !!g.foldable,
        folded: !!g.foldable && business && !holdsHere,
        rows
      }
    })
    .filter((g) => g.rows.length)
}

// The account menu's Admin entry (3a; packages/shared functions/accountMenu.js
// asks it — a named ask for Nika, that file is shared): true when this
// person's role opens at least one /admin page of the rail. A settled 'open'
// only: a page still waiting for its inputs does not show the entry yet.
export function canOpenAdmin(root) {
  const el = this && typeof this.call === 'function' ? this : null
  const r = root && typeof root === 'object' ? root : el && el.state && el.state.root
  if (!el || !r) return false
  const rows = (el.call('adminRailGroups', r) || []).flatMap((g) => g.rows || [])
  return rows.some(
    (row) => row.href.indexOf('/admin') === 0 && el.call('adminPageAccess', r, row.href) === 'open'
  )
}

// Where /admin (Users & Roles) sends a person its own gate keeps out (3a): the
// first page of the rail, in rail order, that their role opens; '' while none
// opens yet and one still waits for its inputs (the landing waits with it);
// '/' when none opens.
export function adminLandingPath(root) {
  const el = this && typeof this.call === 'function' ? this : null
  const r = root && typeof root === 'object' ? root : el && el.state && el.state.root
  if (!el || !r) return '/'
  const rows = (el.call('adminRailGroups', r) || [])
    .flatMap((g) => g.rows || [])
    .filter((row) => row.href.indexOf('/admin/') === 0)
  const answers = rows.map((row) => el.call('adminPageAccess', r, row.href))
  const at = answers.indexOf('open')
  if (at >= 0) return rows[at].href
  return answers.indexOf('pending') >= 0 ? '' : '/'
}
